import type { MeResponse, PermissionKey } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError, get, onUnauthorized, post } from "./api";

interface SessionValue {
  me: MeResponse | null;
  loading: boolean;
  can: (...perms: PermissionKey[]) => boolean;
  signOut: () => Promise<void>;
  /** Message to show on the sign-in screen after being signed out. */
  signedOutReason: string | null;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession() {
  const v = useContext(SessionContext);
  if (!v) throw new Error("SessionProvider missing");
  return v;
}

const WARN_BEFORE_MS = 2 * 60_000;
const PING_EVERY_MS = 60_000;

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [signedOutReason, setReason] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [warning, setWarning] = useState(false);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await get<MeResponse>("/api/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = meQuery.data ?? null;

  useEffect(() => {
    if (me) setExpiresAt(Date.parse(me.sessionExpiresAt));
  }, [me]);

  const clear = useCallback(
    (reason: string | null) => {
      setReason(reason);
      setExpiresAt(null);
      setWarning(false);
      qc.setQueryData(["me"], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
    [qc],
  );

  useEffect(
    () =>
      onUnauthorized((err) => {
        clear(err.code === "session_expired" ? "You were signed out after a period of inactivity." : null);
      }),
    [clear],
  );

  // Activity (typing, clicking) keeps the session alive, checked in once a minute at most.
  const lastPing = useRef(0);
  const ping = useCallback(async () => {
    lastPing.current = Date.now();
    try {
      const res = await post<{ sessionExpiresAt: string }>("/api/session/ping");
      setExpiresAt(Date.parse(res.sessionExpiresAt));
      setWarning(false);
    } catch {
      /* onUnauthorized handles expiry */
    }
  }, []);

  useEffect(() => {
    if (!me) return;
    const onActivity = () => {
      if (Date.now() - lastPing.current > PING_EVERY_MS) void ping();
    };
    const events = ["keydown", "pointerdown", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    lastPing.current = Date.now();
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [me, ping]);

  // Warn shortly before the server will sign them out; sign out locally when it does.
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = expiresAt - Date.now();
      if (left <= 0) clear("You were signed out after a period of inactivity.");
      else setWarning(left <= WARN_BEFORE_MS);
    };
    tick();
    const id = window.setInterval(tick, 5_000);
    return () => window.clearInterval(id);
  }, [expiresAt, clear]);

  const value: SessionValue = {
    me,
    loading: meQuery.isLoading,
    can: (...perms) => !!me && perms.some((p) => me.permissions.includes(p)),
    signOut: async () => {
      await post("/api/auth/sign-out").catch(() => undefined);
      clear(null);
    },
    signedOutReason,
    refresh: async () => {
      setReason(null);
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      {warning && me && <TimeoutWarning onStay={ping} onSignOut={value.signOut} />}
    </SessionContext.Provider>
  );
}

function TimeoutWarning({ onStay, onSignOut }: { onStay: () => void; onSignOut: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4" role="alertdialog" aria-labelledby="timeout-title">
      <div className="w-full max-w-sm rounded-[var(--radius-panel)] bg-white p-6 shadow-xl">
        <h2 id="timeout-title" className="text-lg font-semibold">
          Still there?
        </h2>
        <p className="mt-2 text-ink-soft">For security, you'll be signed out in about two minutes unless you stay.</p>
        <div className="mt-6 flex justify-end gap-2">
          <button className="rounded-[var(--radius-control)] px-4 py-2 font-medium text-ink-soft hover:bg-mist" onClick={onSignOut}>
            Sign out
          </button>
          <button
            autoFocus
            className="rounded-[var(--radius-control)] bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-deep"
            onClick={onStay}
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
